import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import {
  AvailableTransitionDto,
  IssueWorkflowDetailDto,
  IssueWorkflowDto,
  IssueWorkflowStatusDto,
  IssueWorkflowTransitionDto,
  UpdateWorkflowLayoutRequest
} from './issue-workflow.models';

@Injectable({ providedIn: 'root' })
export class IssueWorkflowApiService {
  private readonly api = inject(ApiService);
  private readonly base = 'v1/issue-workflows';

  list(projectId?: string): Observable<IssueWorkflowDto[]> {
    return this.api.get<IssueWorkflowDto[]>(this.base, projectId ? { projectId } : undefined);
  }

  get(id: string): Observable<IssueWorkflowDetailDto> {
    return this.api.get<IssueWorkflowDetailDto>(`${this.base}/${id}`);
  }

  create(body: {
    name: string;
    projectId?: string;
    description?: string;
    isDefault?: boolean;
    createDefaultScheme?: boolean;
    createProcessScheme?: boolean;
  }): Observable<IssueWorkflowDto> {
    return this.api.post<IssueWorkflowDto>(this.base, body);
  }

  delete(id: string): Observable<unknown> {
    return this.api.delete(`${this.base}/${id}`);
  }

  addStatus(workflowId: string, body: {
    name: string;
    category: number;
    mappedIssueStatus: number;
    color?: string;
    isInitial?: boolean;
  }): Observable<IssueWorkflowStatusDto> {
    return this.api.post<IssueWorkflowStatusDto>(`${this.base}/${workflowId}/statuses`, body);
  }

  addTransition(workflowId: string, body: {
    name: string;
    toStatusId: string;
    fromStatusId?: string;
  }): Observable<IssueWorkflowTransitionDto> {
    return this.api.post<IssueWorkflowTransitionDto>(`${this.base}/${workflowId}/transitions`, body);
  }

  addCondition(transitionId: string, body: {
    conditionType: number;
    failureMessage?: string;
    configJson?: string;
  }): Observable<unknown> {
    return this.api.post(`${this.base}/transitions/${transitionId}/conditions`, body);
  }

  addValidator(transitionId: string, body: {
    validatorType: number;
    failureMessage?: string;
    configJson?: string;
  }): Observable<unknown> {
    return this.api.post(`${this.base}/transitions/${transitionId}/validators`, body);
  }

  addAction(transitionId: string, body: {
    actionType: number;
    configJson?: string;
    sortOrder?: number;
  }): Observable<unknown> {
    return this.api.post(`${this.base}/transitions/${transitionId}/actions`, body);
  }

  deleteStatus(workflowId: string, statusId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/${workflowId}/statuses/${statusId}`);
  }

  deleteTransition(workflowId: string, transitionId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/${workflowId}/transitions/${transitionId}`);
  }

  deleteCondition(transitionId: string, conditionId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/transitions/${transitionId}/conditions/${conditionId}`);
  }

  deleteValidator(transitionId: string, validatorId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/transitions/${transitionId}/validators/${validatorId}`);
  }

  deleteAction(transitionId: string, actionId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/transitions/${transitionId}/actions/${actionId}`);
  }

  updateLayout(workflowId: string, body: UpdateWorkflowLayoutRequest): Observable<unknown> {
    return this.api.put(`${this.base}/${workflowId}/layout`, body);
  }

  getAvailableTransitions(issueId: string): Observable<AvailableTransitionDto[]> {
    return this.api.get<AvailableTransitionDto[]>(`v1/issues/${issueId}/available-transitions`);
  }

  executeTransition(issueId: string, transitionId: string, comment?: string): Observable<unknown> {
    return this.api.post(`v1/issues/${issueId}/workflow-transition`, { transitionId, comment });
  }
}
